import React, { useCallback } from 'react';
import { usePathname, useRouter } from 'expo-router';

import type { AddressType } from '@sg/garnish';
import type { DeliveryOrderDetailInput } from '@sg/graphql-schema';

import { useDeliveryOrderInFlight } from '../useDeliveryOrderInFlight';

// ─────────────────────────────────────────────────────────────────────────────

export const useNavigateToMenu = (params: Params = {}) => {
  const { isInsideModal } = params;

  const router = useRouter();
  const pathname = usePathname();

  const shouldDismissAll = isInsideModal || !pathname.includes('/locations');

  const handleGoToMenu = React.useCallback(
    async (restaurantSlug: string, category?: string) => {
      if (shouldDismissAll && router.canDismiss()) {
        router.dismissAll();
      }

      const navigateToMenu = () => {
        router.navigate({
          pathname: '/[restaurantSlug]/menu',
          params: { restaurantSlug, ...(category ? { category } : {}) },
        });
      };

      if (isInsideModal) {
        requestIdleCallback(navigateToMenu);

        return;
      }

      navigateToMenu();
    },
    [isInsideModal, shouldDismissAll, router],
  );

  const handleGoToDeliveryMenu = React.useCallback(
    async (addressId: string, category?: string) => {
      if (shouldDismissAll && router.canDismiss()) {
        router.dismissAll();
      }

      const navigateToDeliveryMenu = () => {
        router.navigate({
          pathname: '/delivery/[addressId]/menu',
          params: { addressId, ...(category ? { category } : {}) },
        });
      };

      if (isInsideModal) {
        requestIdleCallback(navigateToDeliveryMenu);

        return;
      }

      navigateToDeliveryMenu();
    },
    [isInsideModal, shouldDismissAll, router],
  );

  // ─── In Flight Delivery Orders ───────────────────────────────────

  const { inFlightDeliveryOrderId, navigateToDeliveryOrderInFlightScreen } =
    useDeliveryOrderInFlight({ requestPolicy: 'cache-only' });

  return useCallback(
    ({
      orderType,
      restaurantSlug = '',
      deliveryOrderDetail,
      category,
    }: UseNavigateToMenuProps) => {
      if (['pickup', 'outpost'].includes(orderType)) {
        void handleGoToMenu(restaurantSlug, category);

        return;
      }

      // Block going to delivery menu when there is an in-flight delivery order.
      if (inFlightDeliveryOrderId) {
        navigateToDeliveryOrderInFlightScreen();

        return;
      }

      const deliveryType = deliveryOrderDetail as Readonly<{
        address: { id: string };
      }>;
      const addressId = deliveryOrderDetail?.addressId ?? deliveryType?.address?.id;

      if (orderType === 'delivery' && addressId) {
        void handleGoToDeliveryMenu(addressId, category);
      }
    },
    [
      inFlightDeliveryOrderId,
      handleGoToDeliveryMenu,
      handleGoToMenu,
      navigateToDeliveryOrderInFlightScreen,
    ],
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  isInsideModal?: boolean;
};

export type UseNavigateToMenuProps = Readonly<{
  orderType: AddressType;
  restaurantId?: string;
  restaurantSlug?: string;
  restaurantName?: string;
  deliveryAddressName?: string;
  deliveryOrderDetail?: Partial<DeliveryOrderDetailInput>;
  referrer?: MenuScreenReferrer;
  category?: string;
}>;

type MenuScreenReferrer = 'bag';
